import { useState, useCallback } from 'react';
import { user } from '@solimat/solimat-web-endpoint';
import { IBankAccount, IBankAccountCreate, IBankAccountUpdate } from '../../INTERFACES/bankAccount';

const BASE_URL = import.meta.env.VITE_REACT_APP_USUARIOS_PATH || '';
const FUNCTIONS_KEY = import.meta.env.VITE_REACT_APP_USUARIOS_KEY || '';

const MAX_ACCOUNTS = 3;

const useBankAccounts = () => {
  const [accounts, setAccounts] = useState<IBankAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const userService = new user.UserService(BASE_URL, FUNCTIONS_KEY);

  const getBankAccounts = useCallback(async (): Promise<IBankAccount[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getBankAccounts();
      const accountsList = response?.data || response || [];
      // Ordenar para que la cuenta principal aparezca primero
      const sortedAccounts = [...accountsList].sort((a, b) => {
        if (a.principal && !b.principal) return -1;
        if (!a.principal && b.principal) return 1;
        return 0;
      });
      setAccounts(sortedAccounts);
      return sortedAccounts;
    } catch (err: any) {
      console.error('Error fetching bank accounts:', err);
      setError('Error al obtener las cuentas bancarias');
      setAccounts([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getBankAccount = useCallback(async (accountId: string): Promise<IBankAccount | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getBankAccount(accountId);
      return response?.data || response || null;
    } catch (err: any) {
      console.error('Error fetching bank account:', err);
      setError('Error al obtener la cuenta bancaria');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createBankAccount = useCallback(async (accountData: IBankAccountCreate): Promise<boolean> => {
    if (accounts.length >= MAX_ACCOUNTS) {
      setError(`Máximo de ${MAX_ACCOUNTS} cuentas permitidas`);
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await userService.createBankAccount(accountData);
      await getBankAccounts();
      return true;
    } catch (err: any) {
      console.error('Error creating bank account:', err);
      setError('Error al crear la cuenta bancaria');
      return false;
    } finally {
      setLoading(false);
    }
  }, [accounts.length, getBankAccounts]);

  const updateBankAccount = useCallback(async (accountData: IBankAccountUpdate): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await userService.upDateBankAccount(accountData);
      await getBankAccounts();
      return true;
    } catch (err: any) {
      console.error('Error updating bank account:', err);
      setError('Error al actualizar la cuenta bancaria');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getBankAccounts]);

  const deleteBankAccount = useCallback(async (accountId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await userService.deleteBankAccount(accountId);
      await getBankAccounts();
      return true;
    } catch (err: any) {
      console.error('Error deleting bank account:', err);
      setError('Error al eliminar la cuenta bancaria');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getBankAccounts]);

  const setDefaultAccount = useCallback(async (accountId: string): Promise<boolean> => {
    const account = accounts.find(acc => acc.id === accountId);
    if (!account) {
      setError('Cuenta no encontrada');
      return false;
    }

    const updateData: IBankAccountUpdate = {
      ...account,
      principal: true,
    };

    const result = await updateBankAccount(updateData);
    return result !== null;
  }, [accounts, updateBankAccount]);

  const getDefaultAccount = useCallback((): IBankAccount | null => {
    return accounts.find(acc => acc.principal) || null;
  }, [accounts]);

  const getFullIban = (account: IBankAccount): string => {
    return `${account.iban}${account.codigoEntidad}${account.codigoSucursal}${account.digitoControl}${account.numeroCuenta}`;
  };

  const maskIban = (account: IBankAccount): string => {
    const fullIban = getFullIban(account);
    if (!fullIban || fullIban.length < 4) return fullIban;
    const lastFour = fullIban.slice(-4);
    const masked = fullIban.slice(0, -4).replace(/[A-Z0-9]/gi, 'X');
    return `${masked.slice(0, 4)} ${masked.slice(4, 8)} ${masked.slice(8, 12)} ${masked.slice(12, 16)} ${lastFour}`;
  };

  const canAddMore = useCallback((): boolean => {
    return accounts.length < MAX_ACCOUNTS;
  }, [accounts]);

  return {
    accounts,
    loading,
    error,
    getBankAccounts,
    getBankAccount,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
    setDefaultAccount,
    getDefaultAccount,
    getFullIban,
    maskIban,
    canAddMore,
    MAX_ACCOUNTS,
  };
};

export default useBankAccounts;
